import React, { MouseEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import styles from './podcast-widget.module.scss';
import classNames from 'classnames';
import { ReactComponent as Icon15SecBackwards } from '../../assets/icons/icon-15sec-backwards.svg';
import { ReactComponent as Icon15SecForwards } from '../../assets/icons/icon-15sec-forwards.svg';
import { ReactComponent as IconPlay } from '../../assets/icons/icon-play.svg';
import { ReactComponent as IconPause } from '../../assets/icons/icon-pause.svg';
import { ReactComponent as IconVolumeUp } from '../../assets/icons/icon-volume-up.svg';
import { ReactComponent as IconVolumeDown } from '../../assets/icons/icon-volume-down.svg';
import { useViewportWidth } from '../../utils/hooks.utils';

export const PlayPauseButton: React.FC<{ audioRef: React.RefObject<HTMLAudioElement | null> }> = ({ audioRef }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateState = (): void => setIsPlaying(!audio.paused);

    audio.addEventListener('play', updateState);
    audio.addEventListener('pause', updateState);
    audio.addEventListener('ended', updateState); // optional

    return () => {
      audio.removeEventListener('play', updateState);
      audio.removeEventListener('pause', updateState);
      audio.removeEventListener('ended', updateState);
    };
  }, [audioRef]);

  const onClick = (): void => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
  };

  return (
    <button onClick={onClick} aria-label={isPlaying ? 'Pause-Taste' : 'Play-Taste'}>
      {isPlaying && <IconPause />}
      {!isPlaying && <IconPlay />}
    </button>
  );
};

export const BackwardButton: React.FC<{ audioRef: React.RefObject<HTMLAudioElement | null> }> = ({ audioRef }) => {
  const onClick = (): void => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, audio.currentTime - 15);
    }
  };

  return (
    <button onClick={onClick} className={styles.DesktopOnly} aria-label="15 Sekunden zurück">
      <Icon15SecBackwards />
    </button>
  );
};

export const ForwardButton: React.FC<{ audioRef: React.RefObject<HTMLAudioElement | null> }> = ({ audioRef }) => {
  const onClick = (): void => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.min(audio.currentTime + 15, audio.duration);
    }
  };

  return (
    <button onClick={onClick} className={styles.DesktopOnly} aria-label="15 Sekunden vorwärts">
      <Icon15SecForwards />
    </button>
  );
};

export interface PodcastSliderProps {
  position: number;
  duration: number;

  onChangePosition(position: number): void;
}

export const PodcastSlider: React.FC<PodcastSliderProps> = (props) => {
  const screenWidth = useViewportWidth();
  const sliderLineCount: number = useMemo(() => {
    if (screenWidth) {
      if (screenWidth <= 480) {
        return 20;
      }
      if (screenWidth < 1200) {
        return 50;
      }
    }
    return 20;
  }, [screenWidth]);

  const children = useMemo(() => {
    const children = [];

    for (let i = 0; i < sliderLineCount; i++) {
      const height = (Math.sin(i) + 1) / 4 + 0.5;
      const heightString = `${height}rem`;
      const currentPosition = (i / sliderLineCount) * props.duration <= props.position;

      children.push(
        <div
          key={i}
          className={classNames(styles.PodcastWidgetSliderSliderLine, { [styles.CurrentPosition]: currentPosition })}
          style={{ height: heightString }}
        />
      );
    }

    return children;
  }, [props.duration, props.position, sliderLineCount]);

  const onClick: MouseEventHandler = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;

    const newPosition = (clickX / width) * props.duration;
    props.onChangePosition(newPosition);
  };

  const onMove: MouseEventHandler = (e) => {
    if (e.buttons) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;

      const newPosition = (clickX / width) * props.duration;
      props.onChangePosition(newPosition);
    }
  };

  const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (e.touches.length > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const touch = e.touches[0];
      const clickX = touch.clientX - rect.left;
      const width = rect.width;

      const newPosition = (clickX / width) * props.duration;
      props.onChangePosition(newPosition);
    }
  };

  return (
    <div className={styles.PodcastWidgetSliderSlider} onClick={onClick} onMouseMove={onMove} onTouchMove={onTouchMove}>
      {children}
    </div>
  );
};

export interface PodcastVolumeProps {
  volume: number;

  onChangeVolume(volume: number): void;
}

export const PodcastVolume: React.FC<PodcastVolumeProps> = (props) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const div = ref.current;
    if (div) {
      const onKeyDown: (ev: KeyboardEvent) => void = (e) => {
        if (e.key === 'ArrowRight') {
          props.onChangeVolume(Math.min(1, props.volume + 0.1));
        } else if (e.key === 'ArrowLeft') {
          props.onChangeVolume(Math.max(0, props.volume - 0.1));
        }
      };

      div.addEventListener('keydown', onKeyDown);
      return () => div.removeEventListener('keydown', onKeyDown);
    }
  }, [props, ref]);

  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    // iOS doesn't allow changing the volume
    return null;
  }

  const dotLeftPercent = Math.floor(props.volume * 100);
  // eslint-disable-next-line spellcheck/spell-checker
  const dotLeft = `calc(${dotLeftPercent}% - 0.5rem)`;
  const barLeft = `${Math.floor(props.volume * 100)}%`;
  const barRight = `${Math.floor(100 - props.volume * 100)}%`;

  const onClick: MouseEventHandler = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;

    const newVolume = Math.max(0, Math.min(1, clickX / width));
    props.onChangeVolume(newVolume);
  };

  const onMove: MouseEventHandler = (e) => {
    if (e.buttons) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;

      const newVolume = Math.max(0, Math.min(1, clickX / width));
      props.onChangeVolume(newVolume);
    }
  };

  const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (e.touches.length > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const touch = e.touches[0];
      const clickX = touch.clientX - rect.left;
      const width = rect.width;

      const newVolume = Math.max(0, Math.min(1, clickX / width));
      props.onChangeVolume(newVolume);
    }
  };

  return (
    <div
      ref={ref}
      className={styles.PodcastWidgetVolume}
      tabIndex={0}
      role="slider"
      aria-label="Lautstärkeregler"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.floor(props.volume * 100)}
    >
      <IconVolumeDown />
      <div
        className={styles.PodcastWidgetVolumeControl}
        onClick={onClick}
        onMouseMove={onMove}
        onTouchMove={onTouchMove}
      >
        <div className={styles.PodcastWidgetVolumeBar}>
          <div className={styles.PodcastWidgetVolumeBarLeft} style={{ width: barLeft }} />
          <div className={styles.PodcastWidgetVolumeBarRight} style={{ width: barRight }} />
          <div className={styles.PodcastWidgetVolumePoint} style={{ left: dotLeft }} />
        </div>
      </div>
      <IconVolumeUp />
    </div>
  );
};
